import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Clock, ArrowRight, Compass } from "lucide-react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import heroImg from "@/assets/hero-tours.jpg";
import { cn } from "@/lib/utils";

interface Tour {
  id: string;
  slug: string;
  title: string;
  short_description: string | null;
  main_image_url: string | null;
  duration: string | null;
  adult_price: number | null;
  is_featured: boolean | null;
  destination_id: string | null;
  pricing_mode: string | null;
}
interface Destination {
  id: string;
  slug: string;
  title: string;
}

const ToursPage = () => {
  usePageSeo("tours");
  const [tours, setTours] = useState<Tour[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [destFilter, setDestFilter] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      supabase
        .from("tours")
        .select("id,slug,title,short_description,main_image_url,duration,adult_price,is_featured,destination_id,pricing_mode,sort_order")
        .eq("is_active", true)
        .order("is_featured", { ascending: false })
        .order("sort_order", { ascending: true }),
      supabase.from("destinations").select("id,slug,title").eq("is_active", true).order("sort_order"),
    ]).then(([t, d]) => {
      setTours((t.data as Tour[]) || []);
      setDestinations((d.data as Destination[]) || []);
      setLoading(false);
    });
  }, []);

  const filtered = tours.filter((t) => {
    const matchesSearch = !search || t.title.toLowerCase().includes(search.toLowerCase());
    const matchesDest = !destFilter || t.destination_id === destFilter;
    return matchesSearch && matchesDest;
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageHero
        eyebrow="Curated experiences"
        title={<>Caribbean tours, <span className="italic text-gradient-ocean">crafted for you.</span></>}
        subtitle="Saona, Catalina, Samaná, ATV adventures, catamaran days and city escapes — all hand-picked, all unforgettable."
        image={heroImg}
        size="md"
      />

      <section className="container mx-auto px-4 py-16">
        <div className="flex flex-col lg:flex-row gap-6 mb-10">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search tours..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-11 h-12 rounded-full border-border/80"
            />
          </div>
          {destinations.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setDestFilter(null)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium border transition-smooth",
                  !destFilter
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border hover:border-primary/40",
                )}
              >
                All
              </button>
              {destinations.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDestFilter(d.id)}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium border transition-smooth",
                    destFilter === d.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border hover:border-primary/40",
                  )}
                >
                  {d.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] rounded-3xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 max-w-md mx-auto">
            <Compass className="w-12 h-12 mx-auto text-primary/60" />
            <h3 className="font-display text-2xl font-semibold mt-5">New tours coming soon</h3>
            <p className="text-muted-foreground mt-2">
              We're curating the most unforgettable experiences across the island. Check back shortly.
            </p>
            <Link
              to="/transfers"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-gradient-ocean text-primary-foreground font-semibold shadow-glow hover:-translate-y-0.5 transition-smooth"
            >
              Book a transfer instead
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((t) => {
              const dest = destinations.find((d) => d.id === t.destination_id);
              return (
                <Link
                  key={t.id}
                  to={`/tours/${t.slug}`}
                  className="group relative overflow-hidden rounded-3xl bg-card shadow-card hover:shadow-elegant transition-smooth"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-muted">
                    {t.main_image_url ? (
                      <img
                        src={t.main_image_url}
                        alt={t.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-ocean" />
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/95 via-[hsl(var(--hero-bg))]/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                    {t.is_featured && (
                      <span className="inline-block px-2.5 py-0.5 mb-3 text-[10px] uppercase tracking-[0.2em] rounded-full bg-accent text-accent-foreground font-semibold">
                        Featured
                      </span>
                    )}
                    {dest && (
                      <div className="flex items-center gap-1.5 text-xs text-primary-glow uppercase tracking-[0.2em]">
                        <MapPin className="w-3 h-3" />
                        {dest.title}
                      </div>
                    )}
                    <h3 className="font-display text-2xl font-semibold mt-1.5">{t.title}</h3>
                    {t.short_description && (
                      <p className="text-sm text-white/80 mt-1 line-clamp-2">{t.short_description}</p>
                    )}
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-white/70">
                        {t.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {t.duration}
                          </span>
                        )}
                      </div>
                      {t.adult_price ? (
                        <div className="text-right">
                          <div className="text-[10px] text-white/60 uppercase tracking-wider">From</div>
                          <div className="font-display text-xl font-semibold text-primary-glow">
                            ${t.adult_price}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default ToursPage;
